import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useStableDatePickerValue from "shared/hooks/useStableDatePickerValue";
import useStrictDatePickerInput from "shared/hooks/useStrictDatePickerInput";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT } from "utils/constants";

const HINT_AFTER_CLICKS = 3;
const CALENDAR_HEADER = ".ant-picker-header";

const useDatePicker = ({
  errors,
  touched,
  format,
  onChange,
  onBlur,
  value,
  disabledDate,
  onOpenChange,
  onKeyUp,
  onKeyDown,
  ownerDocument,
}) => {
  const { t } = useTranslation(NAME_SPACES.COMPONENTS);
  const texts = t("DATE_PICKER", { returnObjects: true });
  const resolvedFormat = format || DATE_FORMAT;

  const [clickCount, setClickCount] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [hasManualValue, setHasManualValue] = useState(false);

  const {
    inputError,
    applyMask,
    handleCalendarChange,
    handleDateChange,
    handleBlur,
    handleKeyDown,
  } = useStrictDatePickerInput({
    format: resolvedFormat,
    invalidDateMessage: texts.INVALID_DATE,
    disallowedDateMessage: texts.DISALLOWED_DATE,
    disabledDate,
    onChange,
    onBlur,
    value,
  });

  const handleCalendarClick = () => {
    setClickCount((prevCount) => prevCount + 1);
  };

  useEffect(() => {
    if (clickCount >= HINT_AFTER_CLICKS) {
      setShowHint(true);
    }
  }, [clickCount]);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const headers = (ownerDocument || document).querySelectorAll(
      CALENDAR_HEADER,
    );
    for (const header of headers) {
      header.addEventListener("click", handleCalendarClick);
    }

    return () => {
      for (const header of headers) {
        header.removeEventListener("click", handleCalendarClick);
      }
    };
  }, [isCalendarOpen, ownerDocument]);

  const handleOpenChange = (open) => {
    setIsCalendarOpen(open);
    if (!open) {
      setClickCount(0);
      setShowHint(false);
    }
    if (typeof onOpenChange === "function") {
      onOpenChange(open);
    }
  };

  const stableValue = useStableDatePickerValue(value);
  const displayError = inputError || errors;

  useEffect(() => {
    setHasManualValue(!!value);
  }, [value]);

  const handleDateChangeWithManualState = (nextValue, dateString) => {
    handleDateChange(nextValue, dateString);
    setHasManualValue(!!nextValue || !!dateString);
  };

  const handleInputBlur = (event) => {
    handleBlur(event);
    setHasManualValue(!!event?.target?.value?.trim());
  };

  const handleInputKeyUp = (event) => {
    applyMask(event);
    setHasManualValue(!!event?.target?.value?.trim());
    if (onKeyUp) onKeyUp(event);
  };

  const handleInputKeyDown = (event) => {
    applyMask(event);
    handleKeyDown(event);
    if (onKeyDown) onKeyDown(event);
  };

  return {
    texts,
    showHint,
    inputError,
    displayError,
    showError: (touched || !!inputError) && displayError,
    isFilled: !!value || hasManualValue,
    field: {
      format: resolvedFormat,
      disabledDate,
      value: stableValue,
      onChange: handleDateChangeWithManualState,
      onCalendarChange: handleCalendarChange,
      onBlur: handleInputBlur,
      onKeyUp: handleInputKeyUp,
      onKeyDown: handleInputKeyDown,
      onOpenChange: handleOpenChange,
    },
  };
};

export default useDatePicker;
